import LoaderLifestyleX from '@collinsonx/design-system/components/loaderLifestyleX';
import { Flex } from '@collinsonx/design-system/core';
import LayoutError from '@components/LayoutError';
import LoungeError from '@components/LoungeError';
import Maintenance from '@components/Maintenance/Maintenance';
import MetadataWrapper from '@components/MetadataWrapper/MetadataWrapper';
import { getItem } from '@lib';
import {
  hydrateAuthorizedUserContext,
  hydrateUnauthorizedUserContext,
  useLogStorage,
} from '@lib/logging';
import { isFastTrackEntitledUser, useMaintenanceMode } from 'config/features';
import {
  getFastTrackConsentFromStorage,
  isPageProtected,
  isRoute,
  onRouteChange,
  redirectTo,
  ROUTES,
} from 'config/navigation';
import { FASTTRACK_CONSENT_ACCEPTED } from 'config/storage';
import useError from 'hooks/useError';
import useGetUserSession from 'hooks/useGetUserSession';
import useHandleDatadog from 'hooks/useHandleDatadog';
import useHandleEntitlements from 'hooks/useHandleEntitlements';
import useHandleProduct from 'hooks/useHandleProduct';
import { useHandleUserValidation } from 'hooks/useHandleUserValidation';
import useLocale from 'hooks/useLocale';
import usePayload from 'hooks/usePayload';
import useProduct from 'hooks/useProduct';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';

export default function Orchestrator({ children }: { children: ReactNode }) {
  const {
    handleUserValidation,
    isUserValidated,
    shouldValidateUser,
    userValidationDeps,
    userValidationError,
  } = useHandleUserValidation();
  const { getEntitlements, readyToFetchEntitlements } = useHandleEntitlements();
  const { isLocationProduct, loungeCode, payload } = usePayload();
  const { hydrateProductState, productDeps, productDepsLoaded } =
    useHandleProduct();
  const { isFastTrackEnabled, loadingProduct } = useProduct();
  const { error } = useError();
  const translations = useLocale();
  const { sessionHandle, userId, userIsSignedIn } = useGetUserSession();
  const { datadogDeps, handleDatadogUser } = useHandleDatadog();
  const { setUserSessionData } = useLogStorage();
  const { asPath } = useRouter();

  const isPagePublic = !isPageProtected();

  const isFastTrackConsentRoute =
    !isPagePublic && isRoute(ROUTES.FASTTRACK_CONSENT);

  // Verified fast-track consent status. Seeded synchronously from storage
  // presence (so the routing decision is available on first render and never
  // flashes the wrong page) and refined with signature verification in the
  // effect below. `undefined` (public pages) means "not applicable".
  const [consentAccepted, setConsentAccepted] = useState<boolean | undefined>(
    () =>
      isPageProtected() ? !!getItem(FASTTRACK_CONSENT_ACCEPTED) : undefined
  );
  const isConsentResolved = consentAccepted !== undefined;

  // Consent is considered granted when the verified value says so OR a consent
  // token is currently present in storage. Reading storage live keeps the
  // decision fresh immediately after the user accepts (before the async
  // verification re-resolves), which avoids a redirect bounce back to consent.
  const userHasConsented =
    consentAccepted === true || !!getItem(FASTTRACK_CONSENT_ACCEPTED);

  const isEntitledUser =
    isFastTrackEnabled &&
    isLocationProduct &&
    isFastTrackEntitledUser({
      loungeCode,
      sourceCode: payload?.sourceCode,
    });

  // The consent page does not use `product` or the consumer-validation network
  // call, so it must not be gated on `loadingProduct` or `isUserValidated`. It
  // only needs the session + payload + verified consent status to decide what to
  // render (otherwise the loader never clears while validation is in flight).
  const isPageLoading =
    !userIsSignedIn ||
    !payload ||
    (isFastTrackConsentRoute
      ? !isConsentResolved
      : !isUserValidated || loadingProduct);

  useEffect(onRouteChange, []);

  useEffect(() => {
    if (isPagePublic) return;

    let active = true;

    // Re-resolve the verified consent status whenever the session or route
    // settles. `userHasConsented` reads storage live, so the decision is always
    // fresh (e.g. straight after accepting consent) even before this resolves.
    getFastTrackConsentFromStorage().then((accepted) => {
      if (active) setConsentAccepted(accepted);
    });

    return () => {
      active = false;
    };
  }, [isPagePublic, isUserValidated, userIsSignedIn, payload, asPath]);

  useEffect(() => {
    if (!isUserValidated) return;

    hydrateUnauthorizedUserContext();
    hydrateAuthorizedUserContext();
  }, [isUserValidated]);

  useEffect(() => {
    if (isPagePublic) return;
    if (!isUserValidated || !isConsentResolved) return;

    if (isRoute(ROUTES.SHOW_FT_ENTITLEMENT)) {
      if (!isEntitledUser) {
        if (isFastTrackEnabled && isLocationProduct && !userHasConsented) {
          redirectTo(ROUTES.FASTTRACK_CONSENT);
        } else {
          redirectTo(ROUTES.HOME);
        }
      }
      return;
    }

    if (isEntitledUser) {
      redirectTo(ROUTES.SHOW_FT_ENTITLEMENT);
    }
  }, [
    isFastTrackEnabled,
    isUserValidated,
    isConsentResolved,
    consentAccepted,
    isLocationProduct,
    payload?.sourceCode,
    loungeCode,
    isPagePublic,
  ]);

  useEffect(() => {
    if (isPagePublic) return;
    if (!isConsentResolved) return;

    // Leaving the consent page (already consented, or fast-track not applicable)
    // does NOT need consumer validation - decide as soon as the session +
    // payload + consent status are known. Waiting for validation would let the
    // consent page render for a moment before redirecting (a visible flash).
    if (isRoute(ROUTES.FASTTRACK_CONSENT)) {
      if (!userIsSignedIn || !payload) return;

      if (isEntitledUser) {
        redirectTo(ROUTES.SHOW_FT_ENTITLEMENT);
        return;
      }

      if (!isFastTrackEnabled || !isLocationProduct || userHasConsented) {
        redirectTo(ROUTES.HOME);
      }
      return;
    }

    // Redirecting a user TO the consent page from another protected page still
    // waits for validation (the entitlement branch below depends on it).
    if (!isUserValidated) return;

    if (isEntitledUser) return;

    if (isFastTrackEnabled && !userHasConsented) {
      redirectTo(ROUTES.FASTTRACK_CONSENT);
    }
  }, [
    isFastTrackEnabled,
    isUserValidated,
    isConsentResolved,
    consentAccepted,
    isPagePublic,
    isLocationProduct,
    loungeCode,
    userIsSignedIn,
    payload,
    payload?.sourceCode,
  ]);

  useEffect(() => {
    if (!shouldValidateUser) return;

    handleUserValidation();
  }, [...userValidationDeps, shouldValidateUser]);

  useEffect(() => {
    if (isPagePublic) return;

    if (!readyToFetchEntitlements) return;
    getEntitlements();
  }, [readyToFetchEntitlements, isPagePublic]);

  useEffect(() => {
    if (isPagePublic) return;

    if (!productDepsLoaded) return;

    hydrateProductState();
  }, [...productDeps, productDepsLoaded, isPagePublic]);

  useEffect(() => {
    handleDatadogUser();
  }, datadogDeps);

  useEffect(() => {
    setUserSessionData({
      sessionHandle,
      userId,
    });
  }, [sessionHandle, userId]);

  // Fast-track routing only needs the session + payload + verified consent -
  // NOT the product/consumer fetch. Once these are known we can decide the
  // destination up front, so users are never shown the wrong page first.
  const isFastTrackRoutingReady =
    !isPagePublic &&
    isUserValidated &&
    userIsSignedIn &&
    !!payload &&
    isConsentResolved;

  const needsConsentRedirect = isFastTrackEnabled && !userHasConsented;

  // An already-consented (or non-fast-track) user on the consent page must be
  // sent home. This decision is validation-independent (see the redirect effect
  // above), so it is gated on the same signals as the consent-route render -
  // otherwise the consent content would flash before the redirect completes.
  const isPendingConsentRouteExit =
    isFastTrackConsentRoute &&
    userIsSignedIn &&
    !!payload &&
    isConsentResolved &&
    (!isFastTrackEnabled ||
      !isLocationProduct ||
      userHasConsented ||
      isFastTrackEntitledUser({
        loungeCode,
        sourceCode: payload?.sourceCode,
      }));

  const isPendingFastTrackRedirect =
    isPendingConsentRouteExit ||
    (isFastTrackRoutingReady &&
      ((isRoute(ROUTES.SHOW_FT_ENTITLEMENT) && !isEntitledUser) ||
        (!isRoute(ROUTES.SHOW_FT_ENTITLEMENT) &&
          !isRoute(ROUTES.FASTTRACK_CONSENT) &&
          (isEntitledUser || needsConsentRedirect))));

  const showMaintenance = useMaintenanceMode;
  const showError = !showMaintenance && !!error && !isFastTrackConsentRoute;
  const shouldShowChildren =
    !isPendingFastTrackRedirect &&
    !showError &&
    (isPagePublic
      ? !!payload
      : !isPageLoading && (!isFastTrackEnabled || isConsentResolved));
  const showLoader = !showMaintenance && !showError && !shouldShowChildren;

  return (
    <MetadataWrapper>
      {showMaintenance && <Maintenance />}
      {showError && <LayoutError />}
      {showLoader && (
        <Flex
          align="center"
          aria-label={translations.generic.labels.loading}
          h="100%"
          justify="center"
        >
          <LoaderLifestyleX />
        </Flex>
      )}
      <div
        style={{
          display: shouldShowChildren ? 'block' : 'none',
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        {userValidationError && <LoungeError error={userValidationError} />}
        {children}
      </div>
    </MetadataWrapper>
  );
}
